import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DataTableAdvanced, type DataTableColumn, type DataTableFilter } from '@/components/ui/data-table-advanced'
import { Badge } from '@/components/ui/badge'
import { tenantsService, type Tenant } from '@/services/tenants'
import { Plus, Building2, MoreHorizontal, Mail, Phone, Calendar, Edit, Eye, UserX, LogIn, Coins, Trash2, Network, Percent } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { format } from 'date-fns'
import { TenantFormDrawer, TenantFormFooter } from '@/components/tenants/TenantFormDrawer'
import { AddCreditsDrawer, AddCreditsFooter } from '@/components/tenants/AddCreditsDrawer'
import {
  SubAccountsDrawer,
  SubAccountsFooter,
  subAccountsDrawerId,
} from '@/components/tenants/SubAccountsDrawer'
import {
  selectChildren,
  deleteConfirmation,
  describeDeleteError,
} from '@/lib/tenantDeleteRefusal'
import { useDrawer, DrawerFooter, DRAWER_PRESETS } from '@/components/ui/drawer'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { toast } from 'sonner'
import { supabase } from '@/services/supabase'
import {
  commissionConfirmation,
  commissionResultMessage,
  commissionState,
  describeCommissionError,
} from '@/lib/commissionCosting'

export const Tenants: React.FC = () => {
  const { openDrawer, closeDrawer } = useDrawer()
  const { onMessage } = useDrawerMessaging('tenants-page')
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTenants, setSelectedTenants] = useState<Tenant[]>([])

  // Pending confirmation for destructive actions
  const [confirmAction, setConfirmAction] = useState<
    | { type: 'suspend' | 'activate' | 'delete'; tenant: Tenant }
    | { type: 'commission'; tenant: Tenant; enabling: boolean }
    | null
  >(null)

  useEffect(() => {
    fetchTenants()
  }, [])

  // Listen for refresh messages from drawers
  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'refresh') {
        fetchTenants()
      }
    })
  }, [onMessage])

  const fetchTenants = async () => {
    try {
      setLoading(true)
      const data = await tenantsService.list()
      setTenants(data)
    } catch (err) {
      console.error('Failed to fetch tenants:', err)
      toast.error('Failed to load tenants')
    } finally {
      setLoading(false)
    }
  }

  const columns: DataTableColumn<Tenant>[] = [
    {
      accessorKey: 'company_name',
      header: 'Company',
      cell: ({ row }) => {
        const tenant = row.original
        return (
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <div>
              <button
                type="button"
                className="font-medium hover:underline text-left"
                onClick={() => handleViewDetails(tenant)}
              >
                {tenant.company_name}
              </button>
              {tenant.subdomain && (
                <div className="text-sm text-muted-foreground">{tenant.subdomain}.joules.app</div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'contact',
      header: 'Contact',
      cell: ({ row }) => {
        const tenant = row.original
        return (
          <div>
            <div className="font-medium">{tenant.contact_name}</div>
            <div className="flex items-center gap-3 mt-1">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Mail className="h-3 w-3" />
                {tenant.contact_email}
              </div>
              {tenant.contact_phone && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Phone className="h-3 w-3" />
                  {tenant.contact_phone}
                </div>
              )}
            </div>
          </div>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status || 'active'
        return (
          <Badge 
            variant={
              status === 'active' ? 'default' : 
              status === 'trial' ? 'secondary' : 
              'destructive'
            }
          >
            {status}
          </Badge>
        )
      },
      filterFn: 'select',
    },
    {
      accessorKey: 'subscription_plan',
      header: 'Plan',
      cell: ({ row }) => {
        const plan = row.original.subscription_plan || 'Basic'
        return (
          <Badge variant="outline">
            {plan}
          </Badge>
        )
      },
      filterFn: 'select',
    },
    {
      // ADM-1.3. Shown as a column so the state is readable before anyone acts.
      accessorKey: 'commission_costing_enabled',
      header: 'Commission',
      cell: ({ row }) => {
        const state = commissionState(row.original.commission_costing_enabled)
        return (
          <Badge
            variant={state.label === 'On' ? 'default' : 'outline'}
            title={state.label === 'Unknown' ? 'No settings row for this account, or it could not be read' : undefined}
          >
            {state.label}
          </Badge>
        )
      },
      enableSorting: false,
    },
    {
      accessorKey: 'user_count',
      header: 'Users',
      cell: ({ row }) => (
        <div className="text-center">{row.original.user_count || 0}</div>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Calendar className="h-3 w-3" />
          {format(new Date(row.original.created_at), 'MMM d, yyyy')}
        </div>
      ),
    },
  ]

  const filters: DataTableFilter[] = [
    {
      column: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Trial', value: 'trial' },
        { label: 'Suspended', value: 'suspended' },
      ],
    },
    {
      column: 'subscription_plan',
      type: 'select',
      label: 'Plan',
      options: [
        { label: 'Basic', value: 'Basic' },
        { label: 'Pro', value: 'Pro' },
        { label: 'Enterprise', value: 'Enterprise' },
      ],
    },
  ]

  const handleAddTenant = () => {
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: 'tenant-form-new',
      title: 'Create New Tenant',
      content: <TenantFormDrawer onSuccess={() => fetchTenants()} />,
      footer: <TenantFormFooter isEditMode={false} drawerId="tenant-form-new" />,
      size: 'lg',
    })
  }

  const handleEditTenant = (tenant: Tenant) => {
    const drawerId = `tenant-form-${tenant.tenant_id}`
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: drawerId,
      title: `Edit Tenant: ${tenant.company_name}`,
      content: <TenantFormDrawer tenantId={tenant.tenant_id} onSuccess={() => fetchTenants()} />,
      footer: <TenantFormFooter isEditMode={true} drawerId={drawerId} tenantId={tenant.tenant_id} />,
      size: 'lg',
    })
  }

  const handleViewDetails = (tenant: Tenant) => {
    openDrawer({
      ...DRAWER_PRESETS.details,
      id: `tenant-details-${tenant.tenant_id}`,
      title: `Tenant Details: ${tenant.company_name}`,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Company Information
            </h3>
            <dl className="space-y-2">
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">Company Name</dt>
                <dd>{tenant.company_name}</dd>
              </div>
              {tenant.subdomain && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="font-medium text-muted-foreground">Subdomain</dt>
                  <dd className="font-mono">{tenant.subdomain}.joules.app</dd>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">Status</dt>
                <dd>
                  <Badge 
                    variant={
                      tenant.status === 'active' ? 'default' : 
                      tenant.status === 'trial' ? 'secondary' : 
                      'destructive'
                    }
                  >
                    {tenant.status || 'active'}
                  </Badge>
                </dd>
              </div>
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">Subscription Plan</dt>
                <dd>
                  <Badge variant="outline">{tenant.subscription_plan || 'Basic'}</Badge>
                </dd>
              </div>
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">User Count</dt>
                <dd>{tenant.user_count || 0} users</dd>
              </div>
            </dl>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Contact Information
            </h3>
            <dl className="space-y-2">
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">Contact Name</dt>
                <dd>{tenant.contact_name}</dd>
              </div>
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">Email Address</dt>
                <dd className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {tenant.contact_email}
                </dd>
              </div>
              {tenant.contact_phone && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="font-medium text-muted-foreground">Phone Number</dt>
                  <dd className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {tenant.contact_phone}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Important Dates
            </h3>
            <dl className="space-y-2">
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">Created</dt>
                <dd>{format(new Date(tenant.created_at), 'MMMM d, yyyy h:mm a')}</dd>
              </div>
              <div className="flex justify-between py-2 border-b">
                <dt className="font-medium text-muted-foreground">Last Updated</dt>
                <dd>{format(new Date(tenant.updated_at), 'MMMM d, yyyy h:mm a')}</dd>
              </div>
              {tenant.trial_ends_at && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="font-medium text-muted-foreground">Trial Ends</dt>
                  <dd>{format(new Date(tenant.trial_ends_at), 'MMMM d, yyyy')}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      ),
      footer: (
        <DrawerFooter>
          <Button
            variant="outline"
            onClick={() => closeDrawer(`tenant-details-${tenant.tenant_id}`)}
          >
            Close
          </Button>
          <Button onClick={() => handleEditTenant(tenant)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Tenant
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => handleSuspendTenant(tenant)}
          >
            <UserX className="mr-2 h-4 w-4" />
            Suspend Tenant
          </Button>
        </DrawerFooter>
      ),
      size: 'lg',
    })
  }

  const handleSuspendTenant = (tenant: Tenant) => {
    setConfirmAction({
      type: tenant.status === 'suspended' ? 'activate' : 'suspend',
      tenant,
    })
  }

  const handleDeleteTenant = (tenant: Tenant) => {
    setConfirmAction({ type: 'delete', tenant })
  }

  const handleConfirmAction = async () => {
    if (!confirmAction) return
    const { type, tenant } = confirmAction
    setConfirmAction(null)

    if (confirmAction.type === 'commission') {
      try {
        const enabled = await tenantsService.setCommissionCosting(tenant.tenant_id, confirmAction.enabling)
        toast.success(commissionResultMessage(tenant.company_name, enabled))
        fetchTenants()
      } catch (error) {
        console.error('Failed to change commission:', error)
        toast.error(describeCommissionError(error, tenant.company_name), { duration: 12000 })
      }
      return
    }

    try {
      if (type === 'suspend') {
        await tenantsService.suspend(tenant.tenant_id)
        toast.success(`Tenant ${tenant.company_name} has been suspended`)
      } else if (type === 'activate') {
        await tenantsService.activate(tenant.tenant_id)
        toast.success(`Tenant ${tenant.company_name} has been activated`)
      } else {
        await tenantsService.delete(tenant.tenant_id)
        toast.success(`Tenant ${tenant.company_name} has been deleted`)
      }
      fetchTenants()
    } catch (error) {
      console.error(`Failed to ${type} tenant:`, error)
      if (type === 'delete') {
        // SUB-3.11 / FR58. The 409 carries the database's own sentence naming
        // the sub-accounts and the three ways forward. 'Failed to delete
        // tenant' threw away the only part the operator could act on.
        toast.error(describeDeleteError(error, tenant.company_name), {
          duration: 12000,
        })
      } else {
        toast.error('Failed to update tenant status')
      }
    }
  }

  const handleLoginAs = async (tenant: Tenant) => {
    try {
      // Get the main app URL from environment
      const mainAppUrl = import.meta.env.VITE_JOULES_WEB_URL || 'http://localhost:5173'

      // Get current session token from admin panel
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        toast.error('Please refresh the admin panel and try again')
        return
      }

      // Create impersonation token via backend
      const response = await tenantsService.createImpersonationToken(tenant.tenant_id)

      // Redirect to main app with both the session token and impersonation token
      const impersonationUrl = `${mainAppUrl}/impersonate?token=${response.token}&tenant_id=${tenant.tenant_id}&session_token=${session.access_token}&refresh_token=${session.refresh_token}`

      // Open in new tab
      window.open(impersonationUrl, '_blank')

      toast.success(`Logging in as ${tenant.company_name}`)
    } catch (err) {
      console.error('Failed to login as tenant:', err)
      toast.error('Failed to login as tenant')
    }
  }

  const handleAddCredits = (tenant: Tenant) => {
    const drawerId = `add-credits-${tenant.tenant_id}`
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: drawerId,
      title: `Add Credits: ${tenant.company_name}`,
      content: (
        <AddCreditsDrawer
          tenantId={tenant.tenant_id}
          tenantName={tenant.company_name}
          onSuccess={() => fetchTenants()}
        />
      ),
      footer: <AddCreditsFooter drawerId={drawerId} />,
      size: 'md',
    })
  }

  // SUB-2.2 · FR1-FR5, FR68. Enabling sub-accounts on one tenant and setting the cap.
  const handleSubAccounts = (tenant: Tenant) => {
    const drawerId = subAccountsDrawerId(tenant.tenant_id)
    openDrawer({
      ...DRAWER_PRESETS.form,
      id: drawerId,
      title: `Sub-accounts: ${tenant.company_name}`,
      content: (
        <SubAccountsDrawer
          tenantId={tenant.tenant_id}
          tenantName={tenant.company_name}
          onSuccess={() => fetchTenants()}
        />
      ),
      footer: <SubAccountsFooter drawerId={drawerId} />,
      size: 'md',
    })
  }

  const handleExport = async (data: Tenant[]) => {
    try {
      // Create CSV content
      const headers = ['Company Name', 'Subdomain', 'Contact Name', 'Contact Email', 'Status', 'Plan', 'User Count', 'Created']
      const rows = data.map(t => [
        t.company_name,
        t.subdomain ? `${t.subdomain}.joules.app` : '',
        t.contact_name,
        t.contact_email,
        t.status || 'active',
        t.subscription_plan || 'Basic',
        t.user_count || 0,
        format(new Date(t.created_at), 'yyyy-MM-dd')
      ])
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `tenants-${format(new Date(), 'yyyy-MM-dd')}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      
      toast.success('Tenants exported successfully')
    } catch (error) {
      console.error('Failed to export tenants:', error)
      toast.error('Failed to export tenants')
    }
  }

  const renderActions = (tenant: Tenant) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => handleViewDetails(tenant)}>
          <Eye className="mr-2 h-4 w-4" />
          View details
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleEditTenant(tenant)}>
          <Edit className="mr-2 h-4 w-4" />
          Edit tenant
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleAddCredits(tenant)}
        >
          <Coins className="mr-2 h-4 w-4" />
          Add credits
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleSubAccounts(tenant)}>
          <Network className="mr-2 h-4 w-4" />
          Sub-accounts
        </DropdownMenuItem>
        {/* ADM-1.3. Absent, not disabled, when the state is unknown: the RPC
            refuses an account with no settings row. */}
        {commissionState(tenant.commission_costing_enabled).action && (
          <DropdownMenuItem
            onClick={() =>
              setConfirmAction({
                type: 'commission',
                tenant,
                enabling: tenant.commission_costing_enabled !== true,
              })
            }
          >
            <Percent className="mr-2 h-4 w-4" />
            {commissionState(tenant.commission_costing_enabled).action}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleLoginAs(tenant)}
          className="text-primary"
        >
          <LogIn className="mr-2 h-4 w-4" />
          Log in
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className={tenant.status === 'suspended' ? 'text-primary' : 'text-destructive'}
          onClick={() => handleSuspendTenant(tenant)}
        >
          {tenant.status === 'suspended' ? (
            <>
              <Building2 className="mr-2 h-4 w-4" />
              Activate tenant
            </>
          ) : (
            <>
              <UserX className="mr-2 h-4 w-4" />
              Suspend tenant
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive"
          onClick={() => handleDeleteTenant(tenant)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete tenant
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  /**
   * SUB-3.11 / FR58. The delete confirmation's wording, computed from the
   * tenant list this page already holds.
   *
   * ⚠️ THIS DOES NOT GATE THE BUTTON. `tenants` can be seconds out of date, and
   * a client-side gate would be a second definition of a rule the database
   * enforces on a BEFORE DELETE trigger. If this list is stale in the safe
   * direction the operator sees the plain warning and the server refuses them;
   * if it is stale the other way they are warned about a child that has already
   * been released and the delete goes through. Either way the database decides.
   */
  const deleteCopy = deleteConfirmation(
    confirmAction?.tenant.company_name ?? '',
    confirmAction ? selectChildren(tenants, confirmAction.tenant.tenant_id) : [],
  )

  const confirmCopy = !confirmAction
    ? { title: '', description: '', confirmLabel: '', destructive: false }
    : confirmAction.type === 'commission'
      ? commissionConfirmation(confirmAction.tenant.company_name, confirmAction.enabling)
      : confirmAction.type === 'delete'
        ? { ...deleteCopy, destructive: true }
        : confirmAction.type === 'suspend'
          ? {
              title: 'Suspend tenant?',
              description: `${confirmAction.tenant.company_name} and its ${confirmAction.tenant.user_count || 0} users will lose access to the platform.`,
              confirmLabel: 'Suspend',
              destructive: true,
            }
          : {
              title: 'Activate tenant?',
              description: `${confirmAction.tenant.company_name} will regain access to the platform.`,
              confirmLabel: 'Activate',
              destructive: false,
            }

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tenants</h1>
          <p className="text-muted-foreground mt-1">
            Manage all tenant organizations on the platform
          </p>
        </div>
        <Button onClick={handleAddTenant}>
          <Plus className="mr-2 h-4 w-4" />
          Add New Tenant
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Tenants</p>
              <p className="text-2xl font-bold">{tenants.length}</p>
            </div>
            <Building2 className="h-8 w-8 text-muted-foreground opacity-50" />
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">
                {tenants.filter(t => t.status === 'active').length}
              </p>
            </div>
            <Badge variant="default" className="h-6">Active</Badge>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Trial</p>
              <p className="text-2xl font-bold">
                {tenants.filter(t => t.status === 'trial').length}
              </p>
            </div>
            <Badge variant="secondary" className="h-6">Trial</Badge>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Suspended</p>
              <p className="text-2xl font-bold">
                {tenants.filter(t => t.status === 'suspended').length}
              </p>
            </div>
            <Badge variant="destructive" className="h-6">Suspended</Badge>
          </div>
        </div>
      </div>

      {/* Data Table - No border wrapper */}
      <div>
        <DataTableAdvanced
          columns={columns}
          data={tenants}
          loading={loading}
          searchable={true}
          searchPlaceholder="Search tenants..."
          searchKeys={['company_name', 'contact_name', 'contact_email', 'subdomain']}
          filters={filters}
          showColumnVisibility={true}
          showPagination={true}
          showSelection={true}
          pageSize={10}
          pageSizeOptions={[10, 20, 30, 50]}
          onRowSelect={setSelectedTenants}
          onExport={handleExport}
          actions={renderActions}
          emptyMessage="No tenants found."
          toolbar={
            selectedTenants.length > 0 && (
              <Badge variant="secondary">
                {selectedTenants.length} selected
              </Badge>
            )
          }
        />
      </div>

      {/* Confirmation for destructive actions */}
      <ConfirmDialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null)
        }}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmLabel={confirmCopy.confirmLabel}
        destructive={confirmCopy.destructive}
        onConfirm={handleConfirmAction}
      />
    </div>
  )
}